import { useEffect, useRef, useCallback, useState } from 'react';
import * as faceapi from 'face-api.js';
import { FaceDetector, ObjectDetector, FilesetResolver } from '@mediapipe/tasks-vision';
import { uploadVideoChunk } from '../api/uploadService';
import { reportViolation } from '../api/submissionService';
import useObjectDetection from './useObjectDetection';

const FACE_CHECK_INTERVAL = 800;
const MAX_CONSECUTIVE_NO_FACE = Math.ceil(4 * 1000 / FACE_CHECK_INTERVAL);
const AUDIO_CHECK_INTERVAL = 2000;
const MAX_RETRIES = 3;

// Eye tracking / anti-cover constants
const EYE_AR_THRESHOLD = 0.2;          // below this the eye is considered closed/blinked
const EYES_CLOSED_FRAMES = 10;         // ~1.5s of sustained closed/obscured eyes -> treat as absent
const BAD_FRAMES_TO_BLOCK = 3;         // consecutive bad frames needed before blocking (anti-flicker)
const GOOD_FRAMES_TO_RESUME = 6;       // consecutive good frames needed before resuming
const MIN_VIDEO_SIZE = 40;             // below this the camera frame is not usable yet
const DARK_BLOCK_LUMINANCE = 35;       // per-region mean luminance considered "covered"
const COVERED_BLOCK_RATIO = 0.75;      // % of frame regions that must be near-black to call covered

// 68-point gaze / identity constants
const MIN_GAZE_RATIO = 0.25;           // below this the pupil is pushed to the outer eye corner
const MAX_GAZE_RATIO = 0.75;           // above this the pupil is pushed to the inner eye corner
const EXTENDED_GAZE_FRAMES = 12;       // ~1.8s continuous look away -> EXTENDED_GAZE_AWAY violation
const GAZE_AWAY_FRAMES = 20;           // ~3s continuous look away -> treated as absent from the exam
const GAZE_RESUME_FRAMES = 4;          // look-back frames needed before clearing the gaze warning
const IDENTITY_MATCH_DISTANCE = 0.55;  // max landmark-descriptor distance to consider same person
const IDENTITY_FAIL_FRAMES = 6;        // consecutive identity mismatches -> treat as absence

const useProctoring = (videoRef, canvasRef, examId, studentName, isActive, audioContextRef) => {
  const [violations, setViolations] = useState([]);
  const [faceDetected, setFaceDetected] = useState(true);
  const [recording, setRecording] = useState(false);
  const [gazeAway, setGazeAway] = useState(false);
  const [camError, setCamError] = useState(null);
  const [eyesVisible, setEyesVisible] = useState(true);

  const violationsRef = useRef([]);
  const isProctoringRef = useRef(true);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const noFaceCountRef = useRef(0);
  const uploadSessionIdRef = useRef(
    Math.random().toString(36).substring(2) + Date.now().toString(36)
  );
  const uploadQueueRef = useRef(Promise.resolve());
  const frameSkipRef = useRef(0);
  const animFrameRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const audioDataRef = useRef(null);
  const audioCheckRef = useRef(null);
  const lastAudioViolationRef = useRef(0);
  const requestDataIntervalRef = useRef(null);

  const lastViolationTimeRef = useRef({});
  const referenceDescriptorRef = useRef(null);
  const captureEvidenceRef = useRef(null);
  const resetNoFaceRef = useRef(() => {});
  const mediapipeDetectorRef = useRef(null);
  const mediapipeReadyRef = useRef(false);
  const objectDetectorRef = useRef(null);
  const objectDetectorReadyRef = useRef(false);
  const objDetectCounterRef = useRef(0);
  const wasCoveredRef = useRef(false);

  // De-bounce / hysteresis state so a covered camera can never flicker the exam back to active
  const faceDetectedRef = useRef(true);
  const badFramesRef = useRef(0);
  const goodFramesRef = useRef(0);
  const eyesClosedFramesRef = useRef(0);
  const gazeAwayFramesRef = useRef(0);
  const gazeGoodFramesRef = useRef(0);
  const identityFailFramesRef = useRef(0);

  const logViolationInternal = useCallback((type) => {
    const now = Date.now();
    const last = lastViolationTimeRef.current[type] || 0;
    if (now - last < 3000) return;
    lastViolationTimeRef.current[type] = now;

    setViolations(prev => [...prev, { type, timestamp: new Date() }]);

    reportViolation({
      examId,
      studentName,
      type,
      timestamp: new Date().toISOString(),
      uploadSessionId: uploadSessionIdRef.current
    });

    if (captureEvidenceRef.current && ['CAMERA_COVERED', 'MULTIPLE_FACES', 'FACE_CHANGED', 'NO_FACE', 'NOT_IN_FRAME', 'EYES_CLOSED', 'EYES_NOT_VISIBLE', 'EXTENDED_GAZE_AWAY', 'TAB_SWITCH', 'WINDOW_BLUR', 'CELL_PHONE_DETECTED', 'PROHIBITED_OBJECT'].includes(type)) {
      captureEvidenceRef.current(type);
    }
  }, [examId, studentName]);

  const setupAudioMonitoring = useCallback((stream) => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioAnalyserRef.current = analyser;
      audioDataRef.current = new Uint8Array(analyser.frequencyBinCount);

      if (audioContextRef) audioContextRef.current = audioCtx;

      audioCheckRef.current = setInterval(() => {
        if (!audioAnalyserRef.current || !audioDataRef.current) return;
        audioAnalyserRef.current.getByteFrequencyData(audioDataRef.current);
        const avg = audioDataRef.current.reduce((a, b) => a + b, 0) / audioDataRef.current.length;
        if (avg > 80 && Date.now() - lastAudioViolationRef.current > 10000) {
          lastAudioViolationRef.current = Date.now();
          logViolationInternal('AUDIO_ANOMALY');
        }
      }, AUDIO_CHECK_INTERVAL);
    } catch (err) {
      console.error('[AudioMonitor]', err);
    }
  }, [logViolationInternal, audioContextRef]);

  // 68-point landmark gaze estimator.
  // Estimates the horizontal gaze ratio of each eye from where the "pupil" (midpoint of the
  // vertical landmark pairs 38/41 and 43/46) sits between the eye corners. 0.5 = looking at
  // the camera; << 0.5 or >> 0.5 = looking off to the sides.
  const getGazeRatio = useCallback((landmarks) => {
    if (!landmarks || !landmarks.positions || landmarks.positions.length < 48) return null;
    const p = landmarks.positions;

    // Left eye points 36 (outer) / 39 (inner); right eye points 42 (inner) / 45 (outer)
    const leftPupilX = (p[38].x + p[41].x) / 2;
    const rightPupilX = (p[43].x + p[46].x) / 2;
    const leftWidth = (p[39].x - p[36].x) || 1;
    const rightWidth = (p[45].x - p[42].x) || 1;

    const leftRatio = (leftPupilX - p[36].x) / leftWidth;
    const rightRatio = (rightPupilX - p[42].x) / rightWidth;

    // combined, screen-centric ratio (both eyes looking at the same point)
    return (leftRatio + rightRatio) / 2;
  }, []);

  const isCameraCovered = useCallback((canvas) => {
    const w = canvas.width;
    const h = canvas.height;
    if (w < MIN_VIDEO_SIZE || h < MIN_VIDEO_SIZE) return false;

    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;

    // Sample the frame as a grid of regions. A covered lens is detected when the
    // majority of regions are near-black, so partial covers are caught too.
    const cols = 8;
    const rows = 6;
    const bw = Math.max(1, Math.floor(w / cols));
    const bh = Math.max(1, Math.floor(h / rows));
    let darkBlocks = 0;
    for (let by = 0; by < rows; by++) {
      for (let bx = 0; bx < cols; bx++) {
        let sum = 0;
        let count = 0;
        const x0 = bx * bw;
        const y0 = by * bh;
        for (let y = y0; y < y0 + bh; y += 2) {
          for (let x = x0; x < x0 + bw; x += 2) {
            const i = (y * w + x) * 4;
            sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            count++;
          }
        }
        if (count && sum / count < DARK_BLOCK_LUMINANCE) darkBlocks++;
      }
    }
    return darkBlocks / (cols * rows) >= COVERED_BLOCK_RATIO;
  }, []);

  // Hysteresis gate: only flip the emitted faceDetected signal after sustained
  // evidence, so transient noise/blinks cannot reset the exam's block timers.
  const updateFaceState = useCallback((good) => {
    if (good) {
      badFramesRef.current = 0;
      goodFramesRef.current++;
      if (!faceDetectedRef.current && goodFramesRef.current >= GOOD_FRAMES_TO_RESUME) {
        faceDetectedRef.current = true;
        setFaceDetected(true);
      }
    } else {
      goodFramesRef.current = 0;
      badFramesRef.current++;
      if (faceDetectedRef.current && badFramesRef.current >= BAD_FRAMES_TO_BLOCK) {
        faceDetectedRef.current = false;
        setFaceDetected(false);
      }
    }
  }, []);

  // Eye Aspect Ratio from the 6 face-api eye landmark points.
  const computeEAR = useCallback((eye) => {
    if (!eye || eye.length < 6) return 0;
    const a = Math.hypot(eye[1].x - eye[5].x, eye[1].y - eye[5].y);
    const b = Math.hypot(eye[2].x - eye[4].x, eye[2].y - eye[4].y);
    const c = Math.hypot(eye[0].x - eye[3].x, eye[0].y - eye[3].y);
    return c === 0 ? 0 : (a + b) / (2 * c);
  }, []);

  const attemptGetUserMedia = useCallback(async (delay = 0) => {
    if (delay > 0) await new Promise(r => setTimeout(r, delay));
    return navigator.mediaDevices.getUserMedia({ video: true, audio: true });
  }, []);

  const startWebcam = useCallback(async () => {
    setCamError(null);

    const initialDelay = 600;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const stream = await attemptGetUserMedia(attempt === 0 ? initialDelay : 1000);

        videoRef.current.srcObject = stream;
        videoRef.current.style.transform = 'scaleX(-1)';
        streamRef.current = stream;

        setupAudioMonitoring(stream);

        let options = { mimeType: 'video/webm;codecs=vp9,opus' };
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options = { mimeType: 'video/webm;codecs=vp8,opus' };
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            options = { mimeType: 'video/webm' };
          }
        }

        mediaRecorderRef.current = new MediaRecorder(stream, options);
        mediaRecorderRef.current.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            const chunkBlob = e.data;
            const fd = new FormData();
            fd.append('chunk', chunkBlob, 'chunk.webm');
            fd.append('uploadSessionId', uploadSessionIdRef.current);
            uploadQueueRef.current = uploadQueueRef.current.then(() => uploadVideoChunk(fd));
          }
        };
        mediaRecorderRef.current.start();
        requestDataIntervalRef.current = setInterval(() => {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.requestData();
          }
        }, 5000);
        setRecording(true);

        logViolationInternal('EXAM_STARTED');
        return;
      } catch (err) {
        console.error(`[Webcam Attempt ${attempt + 1}/${MAX_RETRIES + 1}]`, err.message);
        if (attempt === MAX_RETRIES) {
          setCamError(
            err.name === 'NotAllowedError'
              ? 'Camera and microphone access denied. Please allow permissions in your browser settings.'
              : 'Could not access camera or microphone. Check your devices and try again.'
          );
        }
      }
    }
  }, [videoRef, logViolationInternal, setupAudioMonitoring, attemptGetUserMedia]);

  const retryWebcam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCamError(null);
    startWebcam();
  }, [startWebcam]);

  const stopWebcam = useCallback(() => {
    isProctoringRef.current = false;
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (audioCheckRef.current) clearInterval(audioCheckRef.current);
    if (requestDataIntervalRef.current) {
      clearInterval(requestDataIntervalRef.current);
      requestDataIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.requestData();
      mediaRecorderRef.current.stop();
    }
    if (audioContextRef && audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    setRecording(false);
  }, [audioContextRef]);

  // React removes and re-creates the <video> element between the verification screen and
  // the exam screen, which drops the live stream and stalls the detection loop. Whenever a
  // new element (or a new stream) appears, re-attach the webcam stream to it so the feed
  // stays visible and the face/eye detection keeps running.
  const lastVideoElRef = useRef(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el !== lastVideoElRef.current || el.srcObject !== streamRef.current) {
      lastVideoElRef.current = el;
      if (streamRef.current && el.srcObject !== streamRef.current) {
        el.muted = true;
        el.srcObject = streamRef.current;
        el.style.transform = 'scaleX(-1)';
        if (typeof el.play === 'function') el.play().catch(() => {});
      }
    }
  });

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm'
        );
        if (cancelled) return;

        // Face detector (primary face presence check)
        const detector = await FaceDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite',
            delegate: 'GPU'
          },
          runningMode: 'VIDEO',
          minDetectionConfidence: 0.6
        });
        if (cancelled) return;
        mediapipeDetectorRef.current = detector;
        mediapipeReadyRef.current = true;

        // Object detector (prohibited items: cell phone, book, laptop)
        try {
          const objDetector = await ObjectDetector.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath:
                'https://storage.googleapis.com/mediapipe-models/object_detector/efficientdet_lite0/float16/1/efficientdet_lite0.tflite',
              delegate: 'GPU'
            },
            scoreThreshold: 0.45,
            maxResults: 5,
            runningMode: 'IMAGE'
          });
          if (cancelled) return;
          objectDetectorRef.current = objDetector;
          objectDetectorReadyRef.current = true;
        } catch (objErr) {
          console.warn('[MediaPipe ObjectDetector] Failed to load', objErr.message);
          objectDetectorReadyRef.current = false;
        }
      } catch (err) {
        console.warn('[MediaPipe] Failed to load, falling back to face-api.js', err.message);
        mediapipeReadyRef.current = false;
        objectDetectorReadyRef.current = false;
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const detectLoop = useCallback(async () => {
    if (!isProctoringRef.current || !videoRef.current || !canvasRef.current) return;

    const detectOnce = async () => {
      frameSkipRef.current++;
      if (frameSkipRef.current % 3 !== 0) {
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // No valid camera frame yet -> keep looping, never flag anything
      if (video.readyState < 2 || (video.videoWidth || 0) < MIN_VIDEO_SIZE || (video.videoHeight || 0) < MIN_VIDEO_SIZE) {
        return;
      }

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const covered = isCameraCovered(canvas);
    if (covered) {
      wasCoveredRef.current = true;
      noFaceCountRef.current++;
      eyesClosedFramesRef.current++;
      setEyesVisible(false);
      logViolationInternal('CAMERA_COVERED');
      updateFaceState(false);

      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ff4444';
      ctx.font = 'bold 36px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('⛔ CAMERA COVERED', canvas.width / 2, canvas.height / 2 - 15);
      ctx.font = 'bold 20px Arial';
      ctx.fillStyle = '#ffcc00';
      ctx.fillText('Your face must be visible at all times', canvas.width / 2, canvas.height / 2 + 25);

      if (noFaceCountRef.current >= MAX_CONSECUTIVE_NO_FACE - 1) {
        ctx.fillStyle = 'rgba(255,0,0,0.3)';
        ctx.fillRect(0, 0, canvas.width, 50);
        ctx.fillStyle = '#ff4444';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('⛔ AUTO-CLOSING EXAM — Camera covered', canvas.width / 2, 34);
      }

      return;
    }
    if (wasCoveredRef.current) {
      wasCoveredRef.current = false;
      noFaceCountRef.current = 0;
      eyesClosedFramesRef.current = 0;
      setEyesVisible(true);
      resetNoFaceRef.current();
    }

    // MediaPipe: highly accurate face presence check (primary detector)
    if (mediapipeReadyRef.current) {
      try {
        const mpResult = mediapipeDetectorRef.current.detectForVideo(video, Date.now());
        const mpFaceDetected = mpResult.detections && mpResult.detections.length > 0;
        if (!mpFaceDetected) {
          noFaceCountRef.current++;
          identityFailFramesRef.current = 0;
          gazeAwayFramesRef.current = 0;
          setEyesVisible(false);
          logViolationInternal('NOT_IN_FRAME');
          updateFaceState(false);

          ctx.fillStyle = 'rgba(255,0,0,0.2)';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = '#ff0000';
          ctx.font = 'bold 38px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('⚠️ FACE NOT FOUND ⚠️', canvas.width / 2, canvas.height / 2 - 15);
          ctx.font = 'bold 20px Arial';
          ctx.fillText('Webcam must show your face at all times', canvas.width / 2, canvas.height / 2 + 25);

          if (noFaceCountRef.current >= MAX_CONSECUTIVE_NO_FACE - 1) {
            ctx.fillStyle = 'rgba(255,0,0,0.3)';
            ctx.fillRect(0, 0, canvas.width, 50);
            ctx.fillStyle = '#ff4444';
            ctx.font = 'bold 24px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('⛔ AUTO-CLOSING EXAM — Face not detected', canvas.width / 2, 34);
          }

          return;
        }
      } catch (mpErr) {
        console.warn('[MediaPipe detect error]', mpErr.message);
      }
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    try {
      const detections = await faceapi.detectAllFaces(video, new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.7 }))
        .withFaceLandmarks().withFaceDescriptors();

      detections.forEach(detection => {
        const box = detection.detection.box;
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 3;
        ctx.strokeRect(box.x, box.y, box.width, box.height);

        const landmarks = detection.landmarks;
        if (landmarks) {
          const eyes = landmarks.getLeftEye();
          landmarks.getRightEye().forEach(p => eyes.push(p));
          ctx.fillStyle = '#00ffff';
          eyes.forEach(p => {
            ctx.beginPath();
            ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
            ctx.fill();
          });
        }
      });

      if (detections.length === 0) {
        noFaceCountRef.current++;
        identityFailFramesRef.current = 0;
        gazeAwayFramesRef.current = 0;
        setEyesVisible(false);
        logViolationInternal('NOT_IN_FRAME');
        updateFaceState(false);

        ctx.fillStyle = 'rgba(255,0,0,0.2)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ff0000';
        ctx.font = 'bold 38px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('⚠️ FACE NOT FOUND ⚠️', canvas.width / 2, canvas.height / 2 - 15);
        ctx.font = 'bold 20px Arial';
        ctx.fillText('Webcam must show your face at all times', canvas.width / 2, canvas.height / 2 + 25);

        if (noFaceCountRef.current >= MAX_CONSECUTIVE_NO_FACE - 1) {
          ctx.fillStyle = 'rgba(255,0,0,0.3)';
          ctx.fillRect(0, 0, canvas.width, 50);
          ctx.fillStyle = '#ff4444';
          ctx.font = 'bold 24px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('⛔ AUTO-CLOSING EXAM — Face not detected', canvas.width / 2, 34);
        }

        return;
      } else if (detections.length > 1) {
        noFaceCountRef.current = 0;
        logViolationInternal('MULTIPLE_FACES');
        setEyesVisible(true);
        resetNoFaceRef.current();
        updateFaceState(true);

        ctx.fillStyle = 'rgba(255,0,0,0.15)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'red';
        ctx.font = 'bold 30px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('⚠️ MULTIPLE FACES DETECTED ⚠️', canvas.width / 2, 40);
      } else {
        noFaceCountRef.current = 0;
        resetNoFaceRef.current();

        const detection = detections[0];
        const landmarks = detection.landmarks;

        // ── EYE TRACKING: the exam only continues while both eyes are visible and open ──
        const leftEye = landmarks && landmarks.getLeftEye ? landmarks.getLeftEye() : null;
        const rightEye = landmarks && landmarks.getRightEye ? landmarks.getRightEye() : null;
        const hasEyes = leftEye && rightEye && leftEye.length >= 6 && rightEye.length >= 6;

        if (!hasEyes) {
          // Face found but eyes cannot be seen -> person is obscured/partially covered
          eyesClosedFramesRef.current++;
          noFaceCountRef.current++;
          setEyesVisible(false);
          logViolationInternal('EYES_NOT_VISIBLE');
          updateFaceState(eyesClosedFramesRef.current < EYES_CLOSED_FRAMES);
        } else {
          const leftEAR = computeEAR(leftEye);
          const rightEAR = computeEAR(rightEye);
          const eyesOpen = leftEAR > EYE_AR_THRESHOLD && rightEAR > EYE_AR_THRESHOLD;

          if (!eyesOpen) {
            eyesClosedFramesRef.current++;
            noFaceCountRef.current++;
            setEyesVisible(false);
            logViolationInternal('EYES_CLOSED');
            updateFaceState(eyesClosedFramesRef.current < EYES_CLOSED_FRAMES);
          } else {
            eyesClosedFramesRef.current = 0;
            noFaceCountRef.current = 0;
            setEyesVisible(true);
            updateFaceState(true);
          }
        }

        // Continuous identity verification via 68-point landmark descriptor matching
        if (detection.descriptor) {
          if (!referenceDescriptorRef.current) {
            referenceDescriptorRef.current = detection.descriptor;
          } else {
            const distance = faceapi.euclideanDistance(referenceDescriptorRef.current, detection.descriptor);
            if (distance <= IDENTITY_MATCH_DISTANCE) {
              identityFailFramesRef.current = 0;
              // slow drift adaptation so lighting/pose changes do not cause false alarms
              referenceDescriptorRef.current = referenceDescriptorRef.current.map(
                (v, i) => v * 0.95 + detection.descriptor[i] * 0.05
              );
            } else {
              identityFailFramesRef.current++;
              noFaceCountRef.current++;
              logViolationInternal('FACE_CHANGED');
              if (identityFailFramesRef.current >= IDENTITY_FAIL_FRAMES) {
                // the person in the frame can no longer be confirmed as the candidate
                setEyesVisible(false);
                updateFaceState(false);
              }
            }
          }
        }

        // Continuous 68-point gaze tracking (off-screen glances + extended look away)
        if (landmarks) {
          const gazeRatio = getGazeRatio(landmarks);
          const lookingAtScreen = gazeRatio !== null && gazeRatio > MIN_GAZE_RATIO && gazeRatio < MAX_GAZE_RATIO;

          if (!lookingAtScreen) {
            gazeAwayFramesRef.current++;
            gazeGoodFramesRef.current = 0;
            setGazeAway(true);
            logViolationInternal('GAZE_AWAY');
            if (gazeAwayFramesRef.current >= EXTENDED_GAZE_FRAMES) logViolationInternal('EXTENDED_GAZE_AWAY');

            ctx.fillStyle = 'rgba(255,255,0,0.1)';
            ctx.fillRect(0, 0, canvas.width, 40);
            ctx.fillStyle = '#ffcc00';
            ctx.font = gazeAwayFramesRef.current >= EXTENDED_GAZE_FRAMES ? 'bold 20px Arial' : 'bold 18px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(gazeAwayFramesRef.current >= EXTENDED_GAZE_FRAMES
              ? '⚠️ Look at the screen — extended glance detected'
              : '⚠️ Look at the screen', canvas.width / 2, 28);

            // extended, uninterrupted look-away == absence from the exam
            if (gazeAwayFramesRef.current >= GAZE_AWAY_FRAMES) {
              noFaceCountRef.current++;
              setEyesVisible(false);
              updateFaceState(false);
            }
          } else {
            gazeGoodFramesRef.current++;
            if (gazeAwayFramesRef.current > 0 && gazeGoodFramesRef.current >= GAZE_RESUME_FRAMES) {
              gazeAwayFramesRef.current = 0;
              gazeGoodFramesRef.current = 0;
              setGazeAway(false);
            }
          }
        }
      }
    } catch (err) {
      console.error('[FaceDetection Error]', err);
      noFaceCountRef.current++;
      logViolationInternal('NO_FACE');
      setEyesVisible(false);
      updateFaceState(false);

      ctx.fillStyle = 'rgba(255,0,0,0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ff0000';
      ctx.font = 'bold 38px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('⚠️ FACE DETECTION ERROR ⚠️', canvas.width / 2, canvas.height / 2 - 15);
      ctx.font = 'bold 20px Arial';
      ctx.fillText('Webcam feed issue — face cannot be verified', canvas.width / 2, canvas.height / 2 + 25);

      if (noFaceCountRef.current >= MAX_CONSECUTIVE_NO_FACE - 1) {
        ctx.fillStyle = 'rgba(255,0,0,0.3)';
        ctx.fillRect(0, 0, canvas.width, 50);
        ctx.fillStyle = '#ff4444';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('⛔ AUTO-CLOSING EXAM — Face detection failed', canvas.width / 2, 34);
      }
    }

    // MediaPipe object detection for prohibited items (every ~60 frames ≈ 3s)
    objDetectCounterRef.current++;
    if (objectDetectorReadyRef.current && objDetectCounterRef.current % 60 === 0) {
      try {
        const objResult = objectDetectorRef.current.detect(video);
        if (objResult.detections && objResult.detections.length > 0) {
          const prohibitedClasses = ['cell phone', 'book', 'laptop'];
          objResult.detections.forEach(det => {
            const cat = det.categories && det.categories[0];
            if (!cat) return;
            const label = cat.categoryName ? cat.categoryName.toLowerCase() : '';
            const score = cat.score || 0;

            if (prohibitedClasses.includes(label)) {
              if (label === 'cell phone') {
                logViolationInternal('CELL_PHONE_DETECTED');
              } else {
                logViolationInternal('PROHIBITED_OBJECT');
              }

              // Draw bounding box
              const box = det.boundingBox;
              if (box) {
                ctx.strokeStyle = '#ff6600';
                ctx.lineWidth = 3;
                ctx.strokeRect(box.originX, box.originY, box.width, box.height);
                ctx.fillStyle = '#ff6600';
                ctx.font = 'bold 14px Arial';
                ctx.textAlign = 'left';
                ctx.fillText(
                  `${cat.categoryName} (${Math.round(score * 100)}%)`,
                  box.originX + 4,
                  box.originY - 6 > 10 ? box.originY - 6 : box.originY + 16
                );
              }
            }
          });
        }
      } catch (objErr) {
        console.warn('[ObjectDetector detect error]', objErr.message);
      }
    }
    };

    try {
      await detectOnce();
    } catch (err) {
      console.error('[detectLoop error]', err);
    } finally {
      animFrameRef.current = requestAnimationFrame(detectLoop);
    }
  }, [videoRef, canvasRef, logViolationInternal, getGazeRatio, isCameraCovered, updateFaceState, computeEAR]);

  useEffect(() => {
    if (!examId || !isActive) return;

    startWebcam();

    const timeout = setTimeout(() => {
      if (isProctoringRef.current) {
        detectLoop();
      }
    }, 2000);

    const handleVisibility = () => {
      if (document.hidden) {
        logViolationInternal('TAB_SWITCH');
        setFaceDetected(false);
        setEyesVisible(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'S' || e.key.toLowerCase() === 's')) {
        e.preventDefault();
        logViolationInternal('BLOCKED: Screenshot attempt');
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibility);
      document.removeEventListener('keydown', handleKeyDown, true);
      stopWebcam();
    };
  }, [examId, isActive, startWebcam, detectLoop, stopWebcam, logViolationInternal]);

  const { handleViolation: captureEvidence, resetNoFaceWarning } = useObjectDetection(videoRef, isActive);
  captureEvidenceRef.current = captureEvidence;
  resetNoFaceRef.current = resetNoFaceWarning;

  return {
    violations,
    violationsRef,
    faceDetected,
    gazeAway,
    recording,
    camError,
    eyesVisible,
    noFaceCount: noFaceCountRef.current,
    uploadSessionId: uploadSessionIdRef.current,
    getUploadQueue: () => uploadQueueRef.current,
    stopWebcam,
    retryWebcam,
    logViolation: logViolationInternal
  };
};

export default useProctoring;
export { MAX_CONSECUTIVE_NO_FACE };
